import { ApolloError, useMutation } from '@apollo/client'
import { useCallback } from 'react'
import {
  CREATE_SESSION,
  RESOLVE_CUSTOMER_VIA_ADD_ON,
  VALIDATE_BY_STORE_CODE,
  type ICreateSession,
  type ICreateSessionVariables,
} from '@commerce/core'

/** Why a login failed, as the screen needs to phrase it. */
export type CreateSessionFailure = 'rejected' | 'unreachable'

export class CreateSessionError extends Error {
  constructor(public readonly reason: CreateSessionFailure) {
    super(`createSession failed: ${reason}`)
  }
}

export interface UseCreateSession {
  /** Resolves to the session id — new, or the reused one for a repeat code. */
  createSession: (slug: string, code: string, phone?: string) => Promise<string>
  loading: boolean
}

/**
 * The real login: `createSession` with the customer resolved by the client
 * system through the storefront's CREATE_SESSION add-on.
 *
 * The store code is the identifier; a phone is an *additional credential*
 * riding in `customFields`, validated by the client system (a mismatch comes
 * back as a GraphQL 404). `data.type` is deliberately unsendable — see
 * `CreateSessionData` in @commerce/core.
 *
 * The two failure modes matter to the screen separately: `rejected` means the
 * client system said no (wrong code or phone — the shopper can fix it), while
 * `unreachable` means nobody answered (retrying is the only fix). An
 * ApolloError with graphQLErrors is the API speaking; one with only a
 * networkError is the network failing to carry the question.
 */
export const useCreateSession = (): UseCreateSession => {
  const [mutate, { loading }] = useMutation<ICreateSession, ICreateSessionVariables>(
    CREATE_SESSION,
  )

  const createSession = useCallback(
    async (slug: string, code: string, phone?: string): Promise<string> => {
      try {
        const { data } = await mutate({
          variables: {
            storefrontName: slug,
            data: {
              code,
              validate: RESOLVE_CUSTOMER_VIA_ADD_ON,
              validateBy: VALIDATE_BY_STORE_CODE,
              ...(phone !== undefined && phone !== ''
                ? { customFields: { phoneNumber: phone } }
                : {}),
            },
          },
        })
        const id = data?.createSession?.id
        if (id === undefined || id === '') throw new CreateSessionError('unreachable')
        return id
      } catch (error) {
        if (error instanceof CreateSessionError) throw error
        if (error instanceof ApolloError && error.graphQLErrors.length > 0) {
          throw new CreateSessionError('rejected')
        }
        throw new CreateSessionError('unreachable')
      }
    },
    [mutate],
  )

  return { createSession, loading }
}
